import { logError } from '../../../utils/logger';

import { FreshTrakLocationsService, isFreshTrakLocationId } from './FreshTrakLocationsService';
import { PlentifulLocationsService } from './PlentifulLocationsService';
import type { Bbox, MapOpts } from './types';

/**
 * Single coherent locations interface for the rest of the app. The Plentiful
 * adapter is always present; the FreshTrak overlay is present in the demo
 * build and absent (null) in the Plentiful-only fallback. When absent the
 * facade delegates to Plentiful alone — no merge, no filtering.
 *
 * The former Lighthouse (PPR) overlay is intentionally not wired here: its API
 * is offline, so this build sources pins from the Plentiful backend plus the
 * static FreshTrak overlay only.
 */
export class LocationsServiceFacade {
  private readonly plentiful: PlentifulLocationsService;

  private readonly freshtrak: FreshTrakLocationsService | null;

  constructor(plentiful: PlentifulLocationsService, freshtrak: FreshTrakLocationsService | null) {
    this.plentiful = plentiful;
    this.freshtrak = freshtrak;
  }

  get isFreshTrakEnabled(): boolean {
    return this.freshtrak !== null;
  }

  async fetchMapLocations(isLoggedIn: boolean, bbox: Bbox, filter: any, opts: MapOpts = {}): Promise<any[]> {
    if (!this.freshtrak) {
      return this.plentiful.fetchMapLocations(isLoggedIn, bbox, filter, opts);
    }

    // Plentiful errors (CORS in local dev hitting prod, 5xx, network) must not
    // hide FreshTrak pins, so the Plentiful leg is caught at the leaf. The
    // FreshTrak overlay is static and cannot fail, so it needs no catch.
    const plentifulP = this.plentiful.fetchMapLocations(isLoggedIn, bbox, filter, opts).catch((e) => {
      logError(e, { context: 'LocationsServiceFacade.fetchMapLocations.plentiful', isLoggedIn });
      return [];
    });
    const freshtrakP = this.freshtrak.fetchMapLocations(bbox);
    const [plentifulRows, freshtrakRows] = await Promise.all([plentifulP, freshtrakP]);
    return this.freshtrak.mergeWith(plentifulRows, freshtrakRows);
  }

  fetchLocationById(id: number | string, isLoggedIn: boolean): Promise<any> {
    if (this.freshtrak && isFreshTrakLocationId(id)) {
      return this.freshtrak.fetchLocationById(id);
    }
    return this.plentiful.fetchLocationById(id, isLoggedIn);
  }
}

export default LocationsServiceFacade;
